/*
  Build a Bundle — state. Holds the bundle, saves it to sessionStorage so
  it survives a refresh, and tells listeners when it changes. Lives on
  window.BuildABundleState.
*/
(function () {
  'use strict';

  // sessionStorage key, namespaced per bundle: wfh:build-a-bundle:{bundleId}
  const STORAGE_PREFIX = 'wfh:build-a-bundle:';

  // State array keys — match the data-step values exactly.
  const STEP_KEYS = ['harnesses', 'dildos', 'addons'];

  // harnesses and dildos are required, addons is optional.
  const REQUIRED_STEPS = ['harnesses', 'dildos'];

  const STEP_MIN_REQUIRED = 1;

  // Each step holds line items:
  //   { variantId, productId, qty, price, compareAtPrice, title, image, size, color }
  const state = {
    bundleId: '',
    harnesses: [],
    dildos: [],
    addons: [],
  };

  // Bundle config read off .js-bundle-root.
  const config = {
    bundleId: '',
    discountPct: 0,
  };

  // event name -> Set of subscriber fns
  const subscribers = new Map();

  let didInit = false;

  /* Pub/sub
     Events: build-a-bundle:hydrated, :state-changed, :item-added,
     :item-removed, :cleared. */

  function subscribe(event, fn) {
    if (!subscribers.has(event)) subscribers.set(event, new Set());
    subscribers.get(event).add(fn);
  }

  function unsubscribe(event, fn) {
    const set = subscribers.get(event);
    if (set) set.delete(fn);
  }

  // A throwing subscriber is logged but doesn't stop the rest.
  function emit(event, detail) {
    const set = subscribers.get(event);
    if (!set) return;
    set.forEach((fn) => {
      try {
        fn(detail);
      } catch (error) {
        console.error('[build-a-bundle] subscriber error: ' + event, error);
      }
    });
  }

  /* sessionStorage persistence */

  function storageKey() {
    return STORAGE_PREFIX + config.bundleId;
  }

  // Safari private mode throws on setItem, so wrap it.
  function persist() {
    try {
      sessionStorage.setItem(
        storageKey(),
        JSON.stringify({
          harnesses: state.harnesses,
          dildos: state.dildos,
          addons: state.addons,
        })
      );
    } catch (error) {
      console.warn('[build-a-bundle] could not persist state', error);
    }
  }

  function hydrateFromStorage() {
    try {
      const raw = sessionStorage.getItem(storageKey());
      if (!raw) return;
      const saved = JSON.parse(raw);
      STEP_KEYS.forEach((key) => {
        state[key] = saved[key] || [];
      });
    } catch (error) {
      console.warn('[build-a-bundle] could not hydrate state', error);
    }
  }

  /* Item helpers */

  function findItem(step, variantId) {
    return state[step].find((item) => item.variantId === variantId);
  }

  function stepQty(step) {
    return state[step].reduce((sum, item) => sum + item.qty, 0);
  }

  function allItems() {
    return state.harnesses.concat(state.dildos, state.addons);
  }

  /* Mutators */

  // Add the variant, or bump its qty by 1 if it's already in this step.
  function addItem(step, payload) {
    if (!state[step]) return;

    const existing = findItem(step, payload.variantId);
    if (existing) {
      existing.qty += 1;
    } else {
      state[step].push({
        variantId: payload.variantId,
        productId: payload.productId,
        qty: 1,
        price: payload.price,
        compareAtPrice: payload.compareAtPrice,
        title: payload.title,
        image: payload.image,
        size: payload.size,
        color: payload.color,
      });
    }

    persist();
    emit('build-a-bundle:item-added', { step, variantId: payload.variantId });
    emit('build-a-bundle:state-changed', { step });
  }

  function removeItem(step, variantId) {
    if (!state[step]) return;
    const before = state[step].length;
    state[step] = state[step].filter((item) => item.variantId !== variantId);
    if (state[step].length === before) return;

    persist();
    emit('build-a-bundle:item-removed', { step, variantId });
    emit('build-a-bundle:state-changed', { step });
  }

  // qty <= 0 removes the variant.
  function updateQty(step, variantId, qty) {
    if (!state[step]) return;
    if (qty <= 0) {
      removeItem(step, variantId);
      return;
    }
    const item = findItem(step, variantId);
    if (!item) return;
    item.qty = qty;

    persist();
    emit('build-a-bundle:state-changed', { step });
  }

  function clear() {
    state.harnesses = [];
    state.dildos = [];
    state.addons = [];

    persist();
    emit('build-a-bundle:cleared', {});
    emit('build-a-bundle:state-changed', {});
  }

  /* Getters — money values are in cents; callers format. */

  function getSubtotal() {
    return allItems().reduce((sum, item) => sum + item.price * item.qty, 0);
  }

  // Full price total before any discount, for the strikethrough.
  function getCompareAtTotal() {
    return allItems().reduce(
      (sum, item) => sum + item.compareAtPrice * item.qty,
      0
    );
  }

  // Baked-in per-product savings — the "up to 35%" marketing figure.
  function getCompareAtSavings() {
    return getCompareAtTotal() - getSubtotal();
  }

  // Discount rate (percent) from the bundle config.
  function resolveDiscountRate() {
    return config.discountPct;
  }

  // Per-unit discounted price, floored per line to match checkout.
  function getItemDiscountedPrice(item) {
    const rate = resolveDiscountRate();
    if (!isComplete() || rate <= 0) return item.price;
    return item.price - Math.floor((item.price * rate) / 100);
  }

  // Extra discount the Shopify Function applies at checkout.
  function getFunctionDiscount() {
    return getSubtotal() - getDiscountedTotal();
  }

  // Sum of floored line prices — reconciles with the rows.
  function getDiscountedTotal() {
    return allItems().reduce(
      (sum, item) => sum + getItemDiscountedPrice(item) * item.qty,
      0
    );
  }

  function isComplete() {
    return REQUIRED_STEPS.every((step) => stepQty(step) >= STEP_MIN_REQUIRED);
  }

  // Deep-clone snapshot so callers can't mutate internals.
  function getState() {
    return JSON.parse(JSON.stringify(state));
  }

  /* Init — read config, restore the saved bundle, then fire the hydrated
     event (both on the pub/sub bus and as a DOM event). Runs once. */
  function init() {
    if (didInit) return;
    didInit = true;

    const root = document.querySelector('.js-bundle-root');
    if (root) {
      config.bundleId = root.dataset.bundleId || '';
      config.discountPct = parseFloat(root.dataset.discountPct) || 0;
    }
    state.bundleId = config.bundleId;

    hydrateFromStorage();

    const snapshot = getState();
    emit('build-a-bundle:hydrated', { state: snapshot });
    document.dispatchEvent(
      new CustomEvent('build-a-bundle:hydrated', {
        detail: { state: snapshot },
      })
    );
  }

  window.BuildABundleState = {
    init,
    addItem,
    removeItem,
    updateQty,
    clear,
    getSubtotal,
    getCompareAtTotal,
    getCompareAtSavings,
    getItemDiscountedPrice,
    getFunctionDiscount,
    getDiscountedTotal,
    isComplete,
    resolveDiscountRate,
    getState,
    subscribe,
    unsubscribe,
  };

  init();
})();
